import React from 'react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { Button, Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle, Spinner, Text } from '../UI';
import { useLogin } from '../../services';
import { useTermsOfService } from '../../services/terms_of_service/terms_of_service';
import { viewAsState } from '../../services/view_as/boot';
import { isHighAccess, permissionOf } from '../../utils/permissions';
import { TermsOfServiceText, TOS_VERSION } from '../../pages/login/TermsOfService.component';

// #region formatHoldsStart
/** 'YYYY-MM-DD' as 'Wednesday, November 4'. Built locally: new Date(str) would parse as UTC and show the day before. */
const formatHoldsStart = (date: string): string => {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

// #region TermsOfServiceGate
/**
 * Non-dismissible gate over every authenticated page until the brand has accepted
 * the current Terms of Service. Until then the backend also holds the brand's
 * orders, so this says so plainly.
 *
 * Only an ADMIN can accept (they bind the company). Other users are told to ask
 * their admin. Staff and "view as customer" sessions never see it, and a status
 * lookup failure fails open here; the order hold is enforced server-side.
 */
export const TermsOfServiceGate: React.FunctionComponent = (): React.ReactElement | null => {
    const [user, , loginFunctions] = useLogin();
    const tos = useTermsOfService();

    const [open, setOpen] = React.useState(false);
    const [holdsStart, setHoldsStart] = React.useState<{ date: string; active: boolean } | null>(null);
    const [checkedFor, setCheckedFor] = React.useState<number | null>(null);
    const [agreed, setAgreed] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [rechecking, setRechecking] = React.useState(false);
    const [submitError, setSubmitError] = React.useState<string | null>(null);

    const isExempt = isHighAccess(user) || viewAsState() !== null;
    const canAccept = permissionOf(user) === 'ADMIN';

    React.useEffect(() => {
        // withAuth already hard-blocks unverified users with its own panel; do not stack on it.
        if (!user?.brand_id || !user.email_verified || isExempt) return;
        if (checkedFor === user.brand_id) return;
        evaluateGate(user.brand_id);
    }, [user, isExempt, checkedFor]);

    // #region evaluateGate
    const evaluateGate = async (brandId: number) => {
        const status = await tos.getStatus();
        setCheckedFor(brandId);
        setOpen(!!status?.needs_acceptance);
        if (status) setHoldsStart({ date: status.holds_start_date, active: status.orders_on_hold });
    };

    // #region handleAccept
    const handleAccept = async () => {
        if (!agreed || submitting) return;
        setSubmitting(true);
        setSubmitError(null);

        const result = await tos.accept(TOS_VERSION);
        setSubmitting(false);

        if (result.ok) setOpen(result.status.needs_acceptance);
        else setSubmitError(result.message);
    };

    // #region handleRecheck
    const handleRecheck = async () => {
        if (!user?.brand_id) return;
        setRechecking(true);
        await evaluateGate(user.brand_id);
        setRechecking(false);
    };

    if (!open) return null;

    // Held already, or a stated deadline. Unknown (older backend) falls back to the held wording.
    const holdLine = holdsStart && !holdsStart.active
        ? `Orders keep being produced until then, but starting ${formatHoldsStart(holdsStart.date)}, orders will be put on hold and will not be produced or shipped until the Terms are accepted.`
        : 'Until they are accepted, orders are on hold and will not be produced or shipped.';

    return (
        <Dialog static={true} open={open} onClose={() => { /* blocking — only accepting closes this */ }} size="4xl" showClose={false}>
            <DialogTitle>
                <div className="flex items-center gap-x-2">
                    <DocumentTextIcon className="h-6 w-6 text-emerald-500" />
                    <span>We&apos;ve updated our Terms of Service</span>
                </div>
            </DialogTitle>
            <DialogDescription>
                {canAccept
                    ? `Please review and accept the new WallMates Terms of Service to keep using WallMates. ${holdLine} Accepting releases any held orders right away.`
                    : `Your brand needs to accept the new WallMates Terms of Service before anyone on the team can keep using WallMates. ${holdLine} Only a brand admin can accept, so please ask your admin to log in and accept them.`}
            </DialogDescription>
            <DialogBody>
                <div className="max-h-[50vh] overflow-y-auto rounded-xl border border-surface-border text-sm">
                    <TermsOfServiceText compact />
                </div>
                {canAccept && (
                    <label className="mt-4 flex items-start gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                        <input type="checkbox" className="mt-0.5 h-4 w-4 shrink-0 rounded border-surface-border" checked={agreed} onChange={(ev) => setAgreed(ev.target.checked)} />
                        <span>I have read and agree to the WallMates Terms of Service, and I have authority to accept them on behalf of my company.</span>
                    </label>
                )}
                {submitError && (
                    <Text className="!text-sm text-red-600 dark:text-red-400 mt-3">{submitError}</Text>
                )}
            </DialogBody>
            <DialogActions>
                <Button plain onClick={() => loginFunctions.logout()}>Log out</Button>
                {canAccept ? (
                    <>
                        <Spinner show={submitting} />
                        <Button color="emerald" disabled={!agreed || submitting} onClick={handleAccept}>
                            {submitting ? 'Saving...' : 'Accept and continue'}
                        </Button>
                    </>
                ) : (
                    <>
                        <Spinner show={rechecking} />
                        <Button color="emerald" disabled={rechecking} onClick={handleRecheck}>My admin has accepted</Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};
